import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Scissors, Store } from "lucide-react";
import { themeOf, tint } from "@/lib/mall";

const money = (n) => `$${(Number(n) || 0).toFixed(0)}`;

/** A real clipped coupon: notched ticket, dashed tear line, printed in the shop's own colors on white. */
export default function CouponCard({ coupon, className = "", notch = "#ffffff" }) {
    const [copied, setCopied] = useState(false);
    const t = themeOf(coupon.store);
    const ink = t.ink || t.primary;

    const copy = async () => {
        try { await navigator.clipboard.writeText(coupon.code); } catch { /* clipboard blocked */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
    };

    return (
        <div className={`relative flex overflow-hidden rounded-[22px] border bg-white transition-transform duration-300 hover:-translate-y-1 ${className}`}
            style={{ borderColor: tint(t.primary, 0.45) }} data-testid={`coupon-${coupon.code}`}>
            <span className="absolute -left-2.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full border" style={{ background: notch, borderColor: tint(t.primary, 0.45) }} />
            <span className="absolute -right-2.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full border" style={{ background: notch, borderColor: tint(t.primary, 0.45) }} />

            <div className="flex w-[34%] shrink-0 flex-col items-center justify-center gap-1 px-3 py-6 text-center"
                style={{ background: tint(t.primary, 0.1) }}>
                <p className="font-display text-2xl font-black leading-none sm:text-3xl" style={{ color: ink }}
                    data-testid={`coupon-value-${coupon.code}`}>
                    {coupon.value_label.replace(" OFF", "")}
                </p>
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500">off</p>
                {coupon.min_subtotal > 0 && (
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                        {money(coupon.min_subtotal)} min
                    </p>
                )}
            </div>

            <div className="min-w-0 flex-1 border-l border-dashed py-5 pl-5 pr-4" style={{ borderColor: tint(t.primary, 0.4) }}>
                <Link to={`/stores/${coupon.store.slug}`} data-testid={`coupon-shop-${coupon.store.slug}`}
                    className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:underline"
                    style={{ color: ink }}>
                    <Store size={10} /> {coupon.store.name}
                </Link>
                <p className="mt-1.5 font-display text-base font-black leading-tight text-zinc-900">{coupon.title}</p>
                {coupon.terms && <p className="mt-1 line-clamp-2 text-xs text-zinc-600">{coupon.terms}</p>}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button onClick={copy} data-testid={`coupon-copy-${coupon.code}`} title="Copy the code" aria-label={`Copy code ${coupon.code}`}
                        className="inline-flex items-center gap-2 rounded-full border border-dashed px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] transition-colors"
                        style={{ borderColor: tint(t.primary, 0.6), color: ink, background: tint(t.primary, 0.08) }}>
                        {copied ? <Check size={11} /> : <Scissors size={11} />} {copied ? "copied" : coupon.code}
                        {!copied && <Copy size={10} className="opacity-60" />}
                    </button>
                    <Link to={`/stores/${coupon.store.slug}`} className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500 hover:text-zinc-900">
                        type it at checkout →
                    </Link>
                </div>
                {coupon.expires_at && (
                    <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                        good through {coupon.expires_at}
                    </p>
                )}
            </div>
        </div>
    );
}
